"use client"

import dynamic from "next/dynamic"
import { useSearchParams } from "next/navigation"
import { useEffect, useRef, useState, type MouseEvent } from "react"
import { AnimatePresence, MotionConfig, motion } from "motion/react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { RefreshCw, WifiOff, Loader2 } from "lucide-react"
import { AnonLimitWall, AnonQuotaNotice, PhotoCapGate, PremiumLimitWall } from "@/components/home/limit-walls"
import { trackEvent, runWhenPageVisible, withResultCtaLocation } from "@/lib/analytics"
import { ANONYMOUS_WEB_ANALYSIS_LIMITS, REGISTERED_FREE_ANALYSIS_LIMITS } from "@/lib/auth/feature-flags"

import { useHomepageAnalysis } from "@/hooks/use-homepage-analysis"
import { ScanRecoveryAccountOffer } from "@/components/home/scan-recovery-account-offer"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import { useAnalysisLawnContext } from "@/hooks/use-analysis-lawn-context"
import { isModifiedNavigationClick } from "@/lib/navigation/modified-click"
import { scrollRefinementTargetIntoView } from "@/lib/navigation/scroll-refinement-target"
import { useFromTool } from "@/hooks/use-from-tool"
import { UploadCard } from "@/components/home"

function LazyDiagnoseFallback({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-auto flex min-h-40 w-full max-w-2xl items-center justify-center gap-3 rounded-2xl border border-green-100 bg-white/75 px-5 text-sm font-medium text-green-900 shadow-sm"
    >
      <Loader2 className="h-5 w-5 animate-spin text-green-600" aria-hidden="true" />
      <span>{label}</span>
    </div>
  )
}

// Post-interaction components — same chunk-splitting rationale as the
// homepage: these only mount after the user uploads a photo.
const SupportingPhotoStep = dynamic(
  () => import('@/components/home/supporting-photo-step').then((mod) => ({ default: mod.SupportingPhotoStep })),
  { loading: () => <LazyDiagnoseFallback label="Loading the photo step..." /> }
)
const AnalysisLoading = dynamic(
  () => import('@/components/home/analysis-loading').then((mod) => ({ default: mod.AnalysisLoading })),
  { loading: () => <LazyDiagnoseFallback label="Preparing your lawn diagnosis..." /> }
)
const DiagnosisResults = dynamic(
  () => import('@/components/home/diagnosis-results').then((mod) => ({ default: mod.DiagnosisResults })),
  { loading: () => <LazyDiagnoseFallback label="Loading your lawn diagnosis..." /> }
)

// The /diagnose flow reuses the homepage analysis hook with the mode locked
// to 'diagnose'. Quota-triggered OAuth returns to this same route, restores the
// persisted photo + ZIP, and auto-runs the diagnosis the user already asked for.
export function DiagnosePageContent() {
  const searchParams = useSearchParams()
  const { lawns } = useDefaultLawn()
  const requestedLawnId = searchParams.get('mode') === 'recheck'
    ? searchParams.get('lawnId')
    : null
  const sourceAnalysisId = searchParams.get('mode') === 'recheck'
    ? searchParams.get('sourceAnalysisId')
    : null
  const recheckTaskId = searchParams.get('mode') === 'recheck'
    ? searchParams.get('taskId')
    : null
  const analysisLawn = useAnalysisLawnContext(lawns, requestedLawnId)
  const {
    uploadedImage,
    uploadedImageUrl,
    isUploading,
    uploadError,
    supportingImages,
    isUploadingSupportingImages,
    supportingImageError,
    isRefiningWithSupportingPhoto,
    supportingPhotoGuidance,
    photoQuality,
    supportingPhotoQuality,
    photoCap,
    refinementPolicy,
    photoCapGate,
    dismissPhotoCapGate,
    handleRefinementGateShown,
    requestSupportingPhotoSignIn,
    isAnalyzing,
    diagnosisResult,
    savedAnalysisId,
    funnelId,
    attemptId,
    originLawnId,
    selectedLawnId,
    limitReached,
    limitTier,
    upgradeRequired,
    analysisNetworkError,
    analysisSaveError,
    analysisServerError,
    zipCode,
    oauthResumeUrl,
    recoveryRedirectUrl,
    prepareRecoverySignIn,
    user,
    anonQuotaReason,
    handleImageUpload,
    handleSupportingImagesUpload,
    removeSupportingImage,
    analyzeImage,
    retryAnalysis,
    resetUpload,
    retryWithNewPhoto,
    refineWithSupportingPhoto,
    clearDiagnosisResult,
    persistDiagnosisForOAuth,
    // Email-capture flow — without these the digest ask on /diagnose is
    // OAuth-only (hasEmailCapture=false). Diagnosis submit unlocks the plan in
    // place (the hook branches on diagnosisResult; no /timeline redirect).
    carePlanEmail,
    carePlanEmailError,
    emailSuggestion,
    isSubmittingCarePlan,
    emailGateCompleted,
    timelineRedirectUrl,
    handleCarePlanEmailChange,
    handleCarePlanEmailKeyPress,
    handleCarePlanSubmit,
    handleResultPrimaryCtaClick,
    handleAcceptEmailSuggestion,
    handleDismissEmailSuggestion,
    navigateToCompletedTimeline,
    authLoading,
    scanUsage,
    authIsPremium,
    subscriptionLoading,
  } = useHomepageAnalysis({
    initialMode: 'diagnose',
    lawnContext: analysisLawn.lawnContext,
  })
  const [recheckResolution, setRecheckResolution] = useState<'idle' | 'saving' | 'resolved' | 'error'>('idle')

  // The empty-state upload view, before the user has committed a photo. Drives
  // the card chrome below.
  const isUploadView = !(uploadedImage || uploadedImageUrl)

  // Named entry event for the web funnel. `diagnosis_landed` previously existed
  // only in the mobile app, so web /diagnose had no funnel-specific top step at
  // all: the next event was `diagnosis_result_landed`, which fires after a
  // photo, a ZIP and an analysis. Everything between arriving and getting a
  // result was invisible, and any cross-platform comparison undercounted web.
  // Held until the tab is visible so prerendered/background loads do not
  // manufacture phantom landings.
  const landedTrackedRef = useRef(false)
  useEffect(() => {
    if (landedTrackedRef.current) return
    landedTrackedRef.current = true
    return runWhenPageVisible(() => {
      trackEvent('diagnosis_landed', { platform: 'web' })
    })
  }, [])

  useEffect(() => {
    if (
      diagnosisResult?.outcome !== 'healthy' ||
      !sourceAnalysisId ||
      !recheckTaskId ||
      !savedAnalysisId ||
      recheckResolution !== 'idle'
    ) return
    setRecheckResolution('saving')
    void fetch('/api/user/diagnosis-lifecycle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceAnalysisId,
        recheckAnalysisId: savedAnalysisId,
        recheckTaskId,
      }),
    }).then((response) => {
      if (!response.ok) throw new Error('resolution failed')
      setRecheckResolution('resolved')
    }).catch(() => setRecheckResolution('error'))
  }, [diagnosisResult?.outcome, recheckResolution, recheckTaskId, savedAnalysisId, sourceAnalysisId])

  // Both refinement destinations sit outside the viewport the user tapped from,
  // so each one is brought into view. Without this the tap reads as a no-op:
  // the gate mounts above a result that is a full viewport tall, and the
  // entitled path replaces the result with the analyzer card while the window
  // stays wherever the CTA was.
  const gateOverResult = Boolean(photoCapGate) && Boolean(diagnosisResult)
  useEffect(() => {
    if (!diagnosisResult || isAnalyzing || gateOverResult) return
    const frame = requestAnimationFrame(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
    return () => cancelAnimationFrame(frame)
  }, [diagnosisResult, isAnalyzing, gateOverResult])
  useEffect(() => {
    if (!gateOverResult) return
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        scrollRefinementTargetIntoView('photo-cap-gate', 'start')
      })
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [gateOverResult])

  useEffect(() => {
    if (!isRefiningWithSupportingPhoto) return
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        scrollRefinementTargetIntoView('analysis-card', 'center')
      })
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [isRefiningWithSupportingPhoto])

  // Warm the chunks the next two steps mount from. The diagnose flow lazy-loads
  // both, so on a cold cache the analyzing card's own fallback spinner is the
  // first thing a committed user sees.
  useEffect(() => {
    if (isUploadView) return
    void import('@/components/home/analysis-loading')
    void import('@/components/home/diagnosis-results')
  }, [isUploadView])

  const registeredExtraDiagnoses = Math.max(
    0,
    REGISTERED_FREE_ANALYSIS_LIMITS.diagnose - ANONYMOUS_WEB_ANALYSIS_LIMITS.diagnose,
  )

  // Attribute referred visits (e.g. ?fromTool=city_chicago-il) so the city -> grass-ID
  // funnel is measurable on the landing side, like every other tool. See use-from-tool.
  useFromTool('diagnose')

  // "Identify my grass instead" from the diagnosis result: hand off to the
  // homepage in identify mode with the photo + ZIP restored via the same
  // resume params the OAuth lane uses.
  const identifyParams = new URLSearchParams()
  if (uploadedImageUrl) {
    identifyParams.set('resume', '1')
    identifyParams.set('imageUrl', uploadedImageUrl)
    supportingImages.forEach((image) => identifyParams.append('supportingImageUrl', image.url))
    if (zipCode) identifyParams.set('zipCode', zipCode)
  }
  const switchToIdentifyHref = identifyParams.size > 0 ? `/?${identifyParams.toString()}` : '/'

  const handleSwitchToIdentify = (event?: MouseEvent<HTMLElement>) => {
    trackEvent('cta_clicked', {
      cta_location: 'diagnose_page_switch_to_identify',
      target_mode: 'identify',
    })
    if (event && isModifiedNavigationClick(event)) return
    clearDiagnosisResult()
  }

  return (
    <MotionConfig reducedMotion="user">
      {/* Flow marker — drives the server-shell landing chrome (methodology
          strip) to hide once the user is past the upload view, via a CSS
          group-has on the page container. Absent on the server render (Suspense
          fallback), so the strip stays in crawlable SSR HTML for crawlers. */}
      {/* `data-diagnose-wall` is a SEPARATE marker, not a third value of
          `data-diagnose-flow`. A photo is always uploaded by the time a wall
          renders, so the flow state is still "active" — and the two blocks in
          page.tsx keyed on `[data-diagnose-flow=active]` must keep hiding.
          Folding a "wall" value into that attribute would silently un-hide them
          at the exact moment the page should be showing one ask.

          It exists so the server-rendered hero (page.tsx owns the H1, outside
          this client tree) can collapse through `group-has-` instead of lifting
          wall state across the server/client boundary. Absent from the server
          render, so the SSR HTML and every crawler always keep the H1. */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="diagnosis-status"
        data-diagnose-flow={uploadedImage || uploadedImageUrl ? "active" : "idle"}
        data-diagnose-wall={limitReached || upgradeRequired || photoCapGate ? "1" : undefined}
      >
        {isUploading && "Uploading image..."}
        {isAnalyzing && "Diagnosing your lawn problem..."}
        {diagnosisResult && (
          diagnosisResult.outcome === 'healthy'
            ? 'Diagnosis complete. The photo shows a healthy lawn.'
            : diagnosisResult.outcome === 'inconclusive'
              ? 'Diagnosis inconclusive. Clearer targeted photos are needed before treatment.'
              : `Diagnosis complete. ${diagnosisResult.problems.length} problem${diagnosisResult.problems.length !== 1 ? 's' : ''} identified.`
        )}
      </div>

      {!diagnosisResult && (
        <div className={cn("mx-auto", isUploadView ? "max-w-[55rem]" : "max-w-2xl")}>
          <Card
            id="analysis-card"
            className={cn(
              "relative z-10 rounded-2xl overflow-hidden",
              // In the upload view the dashed drop target inside UploadCard is
              // the surface — a second white card around it would double the
              // border and reintroduce the framing 2a removes.
              isUploadView
                ? "border-none bg-transparent shadow-none"
                : "bg-white/95 shadow-[0_12px_40px_rgb(0,0,0,0.04)] border border-white/60",
            )}
          >
            {/* limitReached also drops padding: the anon limit wall owns its
                full frame (edge-bleed photo header). Its branch needs sm:p-0
                too — CardContent's base sm:px-6/sm:pb-6 survive a bare p-0
                through tailwind-merge. The upload view keeps bare p-0 so its
                established layout doesn't shift. */}
            <CardContent
              className={
                limitReached ? "p-0 sm:p-0" : isUploadView ? "p-0" : "p-6 md:p-10"
              }
            >
              <AnimatePresence mode="wait" initial={false}>
                {isUploadView ? (
                  <motion.div
                    key="upload"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    {!authLoading && !user && (
                      <AnonQuotaNotice
                        mode="diagnose"
                        reason={anonQuotaReason}
                        extraScans={registeredExtraDiagnoses}
                        redirectPath="/diagnose"
                      />
                    )}
                    <UploadCard
                      onUpload={handleImageUpload}
                      isUploading={isUploading}
                      uploadError={uploadError}
                      mode="diagnose"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="post-upload"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="text-center"
                  >
                    {limitReached ? (
                      <AnonLimitWall
                        mode="diagnose"
                        imageSrc={uploadedImage || uploadedImageUrl}
                        oauthRedirectTo={oauthResumeUrl}
                        extraScans={registeredExtraDiagnoses}
                        appLinkPlacement="diagnose_limit_anon"
                        zipCode={zipCode}
                        tier={limitTier}
                      />
                    ) : upgradeRequired ? (
                      <PremiumLimitWall
                        mode="diagnose"
                        imageSrc={uploadedImage || uploadedImageUrl}
                        appLinkPlacement="diagnose_limit_upgrade"
                      />
                    ) : photoCapGate ? (
                      // Photo-count gate, not a quota wall. Same slot so the
                      // ask lands where the analyzer was.
                      <PhotoCapGate
                        mode={photoCapGate.mode}
                        intent={photoCapGate.intent}
                        trigger={photoCapGate.reason}
                        requiredTier={photoCapGate.requiredTier}
                        cap={photoCapGate.cap}
                        requested={photoCapGate.requested}
                        denialReason={photoCapGate.denialReason ?? null}
                        guidance={refinementPolicy?.nextPhotoGuidance ?? supportingPhotoGuidance}
                        imageSrc={uploadedImage || uploadedImageUrl}
                        oauthRedirectTo={oauthResumeUrl}
                        onDismiss={dismissPhotoCapGate}
                      />
                    ) : !isAnalyzing ? (
                      // Retryable network, server, and save failures keep the photo
                      // in place and offer a one-tap retry.
                      <>
                        {(analysisNetworkError || analysisSaveError || analysisServerError) && (
                          <div
                            role="alert"
                            className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                                {analysisSaveError || analysisServerError ? (
                                  <RefreshCw className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
                                ) : (
                                  <WifiOff className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-amber-900">
                                  {analysisSaveError
                                    ? "Your result wasn't saved"
                                    : analysisServerError
                                      ? "The diagnosis didn't finish"
                                      : "Your connection dropped"}
                                </p>
                                <p className="mt-0.5 text-[13px] text-amber-800/90 leading-snug">
                                  {analysisSaveError
                                    ? "The diagnosis finished, but we could not save the result. Your photo is still here, and this scan was not counted."
                                    : analysisServerError
                                      ? "Your photo is still here, so you can try again."
                                      : "We didn't finish diagnosing your photo. Your photo is still here, so you can pick up right where you left off."}
                                </p>
                                <Button
                                  onClick={retryAnalysis}
                                  className="mt-3 bg-amber-600 hover:bg-amber-700 text-white min-h-[44px] font-semibold"
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
                                  Try again
                                </Button>
                                {!user && recoveryRedirectUrl && (
                                  <ScanRecoveryAccountOffer
                                    redirectTo={recoveryRedirectUrl}
                                    onBeforeRedirect={prepareRecoverySignIn}
                                    mode="diagnose"
                                    reason={analysisSaveError ? 'save' : analysisServerError ? 'server' : 'network'}
                                    funnelId={funnelId}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {!isRefiningWithSupportingPhoto ? (
                          !(analysisNetworkError || analysisSaveError || analysisServerError) &&
                          <AnalysisLoading zipCode={zipCode} imageUrl={uploadedImage || uploadedImageUrl || undefined} mode="diagnose" funnelId={funnelId} attemptId={attemptId} authState={user ? 'authenticated' : 'anonymous'} />
                        ) : <SupportingPhotoStep
                          uploadedImage={uploadedImage || uploadedImageUrl || ''}
                          onAnalyze={analyzeImage}
                          onReset={resetUpload}
                          mode="diagnose"
                          supportingImages={supportingImages}
                          isUploadingSupportingImages={isUploadingSupportingImages}
                          supportingImageError={supportingImageError}
                          onSupportingImagesUpload={handleSupportingImagesUpload}
                          onRemoveSupportingImage={removeSupportingImage}
                          isRefinementStep={isRefiningWithSupportingPhoto}
                          requireSupportingPhoto={isRefiningWithSupportingPhoto}
                          supportingPhotoGuidance={supportingPhotoGuidance}
                          supportingPhotosLocked={!user}
                          onSupportingPhotosSignIn={requestSupportingPhotoSignIn}
                          photoCap={photoCap}
                          lawns={lawns}
                          lawnChoice={analysisLawn.choice ?? null}
                          lawnChoiceRequired={analysisLawn.choiceRequired}
                          lawnChoiceMade={analysisLawn.choiceMade}
                          onLawnChoice={analysisLawn.selectLawn}
                          funnelId={funnelId}
                          authState={user ? 'authenticated' : 'anonymous'}
                        />}
                      </>
                    ) : (
                      <AnalysisLoading zipCode={zipCode} imageUrl={uploadedImageUrl || undefined} mode="diagnose" funnelId={funnelId} attemptId={attemptId} authState={user ? 'authenticated' : 'anonymous'} />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>
      )}

      {diagnosisResult && (
        <div className="space-y-4">
          {/* Refinement sign-in gate for an anonymous visitor who tapped a
              refinement CTA. The result deliberately stays on screen there, so
              the gate cannot live in the analyzer card above — that subtree
              only renders while there is no diagnosis yet. */}
          {photoCapGate && (
            <section id="photo-cap-gate" className="scroll-mt-24 mx-auto w-full max-w-2xl">
              <Card className="relative z-10 bg-white/95 shadow-[0_12px_40px_rgb(0,0,0,0.04)] border border-white/60 rounded-2xl overflow-hidden">
                <CardContent className="p-6 md:p-10">
                  <PhotoCapGate
                    mode={photoCapGate.mode}
                    intent={photoCapGate.intent}
                    trigger={photoCapGate.reason}
                    requiredTier={photoCapGate.requiredTier}
                    cap={photoCapGate.cap}
                    requested={photoCapGate.requested}
                    denialReason={photoCapGate.denialReason ?? null}
                    guidance={refinementPolicy?.nextPhotoGuidance ?? supportingPhotoGuidance}
                    imageSrc={uploadedImage || uploadedImageUrl}
                    oauthRedirectTo={oauthResumeUrl}
                    onDismiss={dismissPhotoCapGate}
                  />
                </CardContent>
              </Card>
            </section>
          )}
          {sourceAnalysisId && recheckTaskId && diagnosisResult.outcome === 'healthy' ? (
            <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
              <span>
                {recheckResolution === 'resolved'
                  ? 'The original issue and its reminders are marked resolved.'
                  : recheckResolution === 'error'
                    ? "We couldn't update the original issue. Your result is safe."
                    : 'Updating the original issue...'}
              </span>
              {recheckResolution === 'error' ? (
                <Button size="sm" variant="outline" onClick={() => setRecheckResolution('idle')}>
                  Retry
                </Button>
              ) : null}
            </div>
          ) : null}
          <DiagnosisResults
          subscriptionLoading={subscriptionLoading}
          isPremium={authIsPremium || Boolean(scanUsage?.isPremium)}
          result={diagnosisResult}
          uploadedImage={uploadedImage || ''}
          zipCode={zipCode}
          savedAnalysisId={savedAnalysisId}
          funnelId={funnelId}
          attemptId={attemptId}
          originLawnId={originLawnId}
          selectedLawnId={selectedLawnId}
          zipMatch={analysisLawn.selectedLawn?.zip_code
            ? analysisLawn.selectedLawn.zip_code === zipCode
            : null}
          onReset={retryWithNewPhoto}
          onRefineWithSupportingPhoto={refineWithSupportingPhoto}
          refinementLocked={!user}
          refinementPending={Boolean(photoCapGate)}
          refinementPolicy={refinementPolicy}
          onRefinementGateShown={handleRefinementGateShown}
          onSwitchToIdentify={handleSwitchToIdentify}
          switchToIdentifyHref={switchToIdentifyHref}
          user={user}
          authLoading={authLoading}
          redirectUrl={oauthResumeUrl}
          onBeforeOAuth={persistDiagnosisForOAuth}
          carePlanEmail={carePlanEmail}
          carePlanEmailError={carePlanEmailError}
          emailSuggestion={emailSuggestion}
          isSubmittingCarePlan={isSubmittingCarePlan}
          emailGateCompleted={emailGateCompleted}
          timelineRedirectUrl={timelineRedirectUrl}
          onEmailChange={handleCarePlanEmailChange}
          onEmailKeyPress={handleCarePlanEmailKeyPress}
          onEmailSubmit={handleCarePlanSubmit}
          onPrimaryCtaClick={withResultCtaLocation("diagnose_page_result", handleResultPrimaryCtaClick)}
          onAcceptEmailSuggestion={handleAcceptEmailSuggestion}
          onDismissEmailSuggestion={handleDismissEmailSuggestion}
          onSeeCarePlan={navigateToCompletedTimeline}
          />
        </div>
      )}
    </MotionConfig>
  )
}
